/*
        classCreated: namespace =>
        {

        }
*/
import { PartialNullable } from "../../Extensions";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyInt32, PropertyString } from "../../RadObject";
import { DialogResultEnum } from "../Dialogs/DialogResultEnum";
import { Button } from "./Button";
import { ButtonStyleEnum } from "./ButtonStyleEnum";
import { Dialog, DialogPropertyValues, IDialog } from "./Dialog";
import { InfiniteScrollPanel } from "./InfiniteScrollPanel";
import { Table } from "./LazyDataTable";
import { Panel } from "./Panel";
import { TextBox } from "./TextBox";
import { NAMESPACE } from "./_Namespace";

import "./PickerDialog.scss";

// Obsolete
export type PickerDialogPropertyValues =
    DialogPropertyValues
    & {
        addNew: boolean;
        columns: any[];
        dataServiceId: string;
        dataServiceOperation: string;
        returnProperty: string;
        topN: number;
    };

export type PickerDialogProperties = {
    [name in keyof PickerDialogPropertyValues]: Property<PickerDialogPropertyValues[name]>
};

export interface IPickerDialog extends IDialog, PickerDialogProperties
{
}

export abstract class PickerDialog extends Dialog implements IPickerDialog
{
    // #region Static

    public static override classNamespace = NAMESPACE + "PickerDialog";
    public static override className = "PickerDialog";

    // #region Constructor

    static {
        $$(document).on("click", ".rad-dialog-picker a.rad-action-select", (evt: IJNativeEvent) =>
        {
            const
                $$currentTarget = $$(evt.currentTarget),
                page = $$currentTarget.closest(".rad-control-page").radControl() as PickerDialog | undefined,
                data = $$currentTarget.closest("tr").data();

            if (page && data)
                page._selectAsync(data);
        });
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<PickerDialogPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                addNew: true,
                buttonStyle: ButtonStyleEnum.okCancel,
                returnProperty: "id",
                scrollable: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _queueLoadLazyDataTableAsyncTimeoutId?: number;
    protected $$iconElement?: JNative;
    protected buttonCancel?: Button;
    protected infiniteScrollPanel?: InfiniteScrollPanel;
    protected lazyDataTable?: Table;
    protected loadingMore?: boolean;
    protected panelButtonContainer?: Panel;
    protected panelSearch?: Panel;
    protected textBoxSearch?: TextBox;

    // #endregion

    // #region Properties

    public addNew!: PropertyBoolean;
    public columns!: Property<any[]>;
    public dataServiceId!: PropertyString;
    public dataServiceOperation!: PropertyString;
    public returnProperty!: PropertyString;
    public topN!: PropertyInt32;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<PickerDialogPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-dialog-picker");

        // Trigger Property Changes
        this.addNew.triggerChange();
        this.columns.triggerChange();
        this.dataServiceId.triggerChange();
        this.dataServiceOperation.triggerChange();
        this.topN.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _addNewChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _checkLoadedEnough()
    {
        return;
        if (!this._isShown())
            return;

        if (this.infiniteScrollPanel?.isContentScrollable())
            return;

        this._loadLazyDataTableAsync();
    }
    protected _columnsChanged(change: PropertyChange<any[]>)
    {
        this.lazyDataTable?.columns.set(change.currentValue);
    }
    protected _dataServiceIdChanged(change: PropertyChange<string>)
    {
        this.lazyDataTable?.dataServiceId.set(change.currentValue);
    }
    protected _dataServiceOperationChanged(change: PropertyChange<string>)
    {
        this.lazyDataTable?.dataServiceOperation.set(change.currentValue);
    }
    protected async _loadLazyDataTableAsync(clear?: boolean)
    {
        if (clear)
            this.lazyDataTable?.clearData();

        this.lazyDataTable?.updateAdditionalDataServiceParameters({
            searchText: this.textBoxSearch?.value.get()
        });

        await this.lazyDataTable?.loadDataAsync();
    }
    protected _queueLoadLazyDataTableAsync()
    {
        if (this._queueLoadLazyDataTableAsyncTimeoutId)
            window.clearTimeout(this._queueLoadLazyDataTableAsyncTimeoutId);

        this._queueLoadLazyDataTableAsyncTimeoutId =
            window.setTimeout(() =>
            {
                this._loadLazyDataTableAsync(true);
            }, 300);

    }
    protected _returnPropertyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected async _selectAsync(data: any)
    {
        const
            returnProperty = this.returnProperty.get();

        this.promiseData = returnProperty ? data[returnProperty] : data;
        this.dialogResult = DialogResultEnum.ok;

        await this.closeAsync();

    }
    protected _topNChanged(change: PropertyChange<number>)
    {
        this.lazyDataTable?.topN.set(change.currentValue);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: PickerDialogPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.addNew = new PropertyBoolean(this, propertyValues.addNew, this._addNewChanged);
        this.columns = new Property<any[]>(this, "System.Object, System.Private.CoreLib", propertyValues.columns, this._columnsChanged);
        this.dataServiceId = new PropertyString(this, propertyValues.dataServiceId, this._dataServiceIdChanged);
        this.dataServiceOperation = new PropertyString(this, propertyValues.dataServiceOperation, this._dataServiceOperationChanged);
        this.returnProperty = new PropertyString(this, propertyValues.returnProperty, this._returnPropertyChanged);
        this.topN = new PropertyInt32(this, propertyValues.topN, this._topNChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this.buttonCancel?.on("click", { self: this }, this.onButtonCancelClicked);
        this.infiniteScrollPanel?.on("loadMore", { self: this }, this.onLoadMore);
        this.lazyDataTable?.on("dataCleared", { self: this }, this.onLazyDataTableDataCleared);
        this.lazyDataTable?.on("dataLoaded", { self: this }, this.onLazyDataTableDataLoaded);
        this.textBoxSearch?.on("valueChanged", { self: this }, this.onTextBoxSearchValueChanged);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.$$iconElement =
            JNativeStatic.create$$({
                nodeName: "i",
                attributes: { "class": "icon rad-icon-default-32" },
                children: [{ nodeName: "span" }]
            }).appendTo(this._titleElementRef.current);

        this.panelSearch =
            new Panel({ cssClass: "rad-panel-search" })
                .appendTo(this);

        this.textBoxSearch =
            new TextBox({ cssClass: "rad-input-search", propagateInputEvents: false, svgKey: "icon-search" })
                .appendTo(this.panelSearch);

        this.panelButtonContainer =
            new Panel({ cssClass: "rad-panel-button-container" })
                .appendTo(this.panelSearch);

        this.buttonCancel =
            new Button({ cssClass: "rad-action-cancel", inline: true, text: "Cancel" })
                .appendTo(this.panelButtonContainer);

        this.infiniteScrollPanel =
            new InfiniteScrollPanel({ hasPreviousResults: false })
                .appendTo(this);

        this.lazyDataTable =
            new Table()
                .appendTo(this.infiniteScrollPanel);

        return $$element;
    }
    protected override async _loadAsync()
    {
        await super._loadAsync();
        await this._loadLazyDataTableAsync();
    }

    // #endregion

    // #region Event Handlers

    protected async onButtonCancelClicked(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.dialogResult = DialogResultEnum.cancel;
        await self.closeAsync();
    }
    protected onLazyDataTableDataCleared(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.infiniteScrollPanel?.hasNextResults.set(true);
    }
    protected onLazyDataTableDataLoaded(evt: IJNativeEvent, eventData: any)
    {
        const
            self = evt.data.self as this,
            hasNextResults = eventData && eventData.results && eventData.results.length && eventData.results[0].length === self.topN.get();

        self.infiniteScrollPanel?.hasNextResults.set(hasNextResults);

        if (hasNextResults)
            self._checkLoadedEnough();
    }
    protected onLoadMore(evt: IJNativeEvent, eventData: any)
    {
        if (eventData.location !== "bottom")
            return;

        const
            self = evt.data.self as this;

        if (self.loadingMore)
            return;

        self.loadingMore = true;

        self._loadLazyDataTableAsync()
            .finally(() =>
            {
                self.loadingMore = false;
                eventData.callback();
            });
    }
    protected onTextBoxSearchValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._queueLoadLazyDataTableAsync();
    }

    // #endregion

    // #endregion
}

